import { subtrackLabel } from '../../logic/subtracks';
import {
  alertLine, goalRing, lastReadingLine, nextDueLine, outreachLines, visitLines, type RosterLine,
} from '../../logic/roster';
import {
  lastInboundViaClinicLine, type LegacyDashboardRow, type RosterNextDue, type RosterVisit,
} from '../../logic/dashboard';

function Line({ line, sub = false }: { line: RosterLine; sub?: boolean }) {
  return <span className={`roster-cell__${sub ? 'sub' : 'main'} roster-tone-${line.tone}`}>{line.text}</span>;
}

export function SubtrackCell({ code }: { code: string | null }) {
  if (!code) return <span className="roster-cell__main roster-tone-muted">Not set</span>;
  const label = subtrackLabel(code);
  return (
    <span className="roster-cell" title={label ? `${code} · ${label}` : code}>
      <span className="roster-cell__main">{code}</span>
      {label && <span className="roster-cell__sub roster-tone-muted">{label}</span>}
    </span>
  );
}

// `now` comes from the table's one useNow(), not a clock per cell.
export function NextDueCell({ nextDue, now }: { nextDue: RosterNextDue | null; now: number }) {
  const line = nextDueLine(nextDue, now);
  return (
    <span className="roster-cell" title={nextDue?.title ?? undefined}>
      <Line line={line} />
      {nextDue?.title && <span className="roster-cell__sub roster-tone-muted">{nextDue.title}</span>}
    </span>
  );
}

export function VisitCell({ visit, now }: { visit: RosterVisit | null; now: number }) {
  const lines = visitLines(visit, now);
  return (
    <span className="roster-cell" title={lines.title ?? undefined}>
      <Line line={lines.last} />
      <Line line={lines.next} sub />
    </span>
  );
}

// Unread: a blue dot before the name, the name in bold, and the message preview in the name's tooltip.
export function NameCell({
  first, last, unread, blur,
}: {
  first?: string; last?: string; unread?: LegacyDashboardRow['unread'] | null; blur: boolean;
}) {
  const fullName = `${first || ''} ${last || ''}`.trim();
  const isUnread = !!unread?.has_unread;
  const preview = unread?.snippet || 'New message';
  const said = lastInboundViaClinicLine(unread) ? `Clinic line · sender unverified: ${preview}` : preview;
  return (
    <span className="roster-name">
      {isUnread ? (
        <span className="roster-name__dot" title={said}>
          <span className="sr-only">Unread</span>
        </span>
      ) : <span className="roster-name__dot is-none" aria-hidden="true" />}
      <span className={`roster-name__text${isUnread ? ' is-unread' : ''}${blur ? ' demo-blur' : ''}`} title={isUnread ? `${fullName} · ${said}` : fullName}>
        {fullName || '—'}
      </span>
    </span>
  );
}

export function AlertStatusCell({ level, reason, hasReading }: {
  level?: string | null; reason?: string | null; hasReading: boolean;
}) {
  const line = alertLine(level, reason, hasReading);
  return (
    <span className="roster-alert" title={`${line.level}: ${line.text}`}>
      <span className={`alert-dot is-${line.dot}`} aria-hidden="true" />
      <span className="sr-only">{`${line.level}: `}</span>
      <span className={`roster-cell__main roster-tone-${line.tone}`}>{line.text}</span>
    </span>
  );
}

const RING_R = 9;
const RING_C = 2 * Math.PI * RING_R;

export function GoalsRingCell({ oap }: { oap: Parameters<typeof goalRing>[0] }) {
  const ring = goalRing(oap);
  if (!ring) return <span className="roster-cell__main roster-tone-muted">—</span>;
  return (
    <span className="goals-ring-cell" title={ring.label}>
      <svg className={`goal-ring is-${ring.tone}${ring.dashed ? ' is-missing' : ''}`} width="24" height="24" viewBox="0 0 24 24" aria-hidden="true">
        <circle className="goal-ring__track" cx="12" cy="12" r={RING_R} />
        {ring.fraction > 0 && (
          <circle className="goal-ring__arc" cx="12" cy="12" r={RING_R} strokeDasharray={`${ring.fraction * RING_C} ${RING_C}`} />
        )}
      </svg>
      <span className="roster-cell">
        <span className="roster-cell__main">{ring.main}</span>
        <span className="sr-only">{ring.label}</span>
        {ring.reason && <span className="roster-cell__sub roster-tone-muted">{ring.reason}</span>}
      </span>
    </span>
  );
}

export function OutreachCell({ humanAt, aiAt, now }: { humanAt: unknown; aiAt: unknown; now: number }) {
  const lines = outreachLines(humanAt, aiAt, now);
  return (
    <span className="roster-cell" title={lines.title}>
      <span className="sr-only">Care team: </span>
      <Line line={lines.human} />
      <Line line={lines.ai} sub />
    </span>
  );
}

export function LastReadingCell({ epoch, now }: { epoch: unknown; now: number }) {
  const line = lastReadingLine(epoch, now);
  return <span className={`roster-cell__main roster-tone-${line.tone}`}>{line.text}</span>;
}
